// useReviewQueue — the review-queue page's view of "pending agent reviews".
//
// The queue merges two sources. The first is the AgentTask rows with
// `status = 'review_required'`. The second is the persisted artefact rows with
// `reviewStatus = 'pending'`, because ATLAS post-call COMPLETES its task and
// writes its artefacts into their own tables, so a task-only badge undercounts.
//
// The RULE that turns those two sources into review items — the grain of an
// item, the frontend it lands on, and which task envelopes are suppressed —
// does not live here. It lives once in `@septima/contract`
// (`review-queue-projection.ts`), because the sidebar badge counts the same
// items on the server (`ReviewQueueService.getCounts`). The rule used to be
// written twice, with a comment as the only bond, and the two copies drifted
// until six task types counted zero on the badge while they showed here.
//
// What this module adds is PRESENTATION: the agent eyebrow, the two label
// lines and the context link. Only the web renders those, so only the web
// holds them.

import type {
    AgentFrontend,
    AgentRowTable,
    AgentTask,
    ArtefactSummaryRow,
    CallArtefactRow,
    CoacActionDraftRow,
    CoacNoticeDate,
    ComposerDraftAgent,
    MonthlyNarrativeRow,
    RelationshipProposalRow,
} from '@septima/contract';
import { agentRowTableFor, artefactTableNameFor, projectReviewQueue } from '@septima/contract';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAgent } from '@/contexts/AgentContext';
import { api } from '@/lib/api';
// `agent-rows/hooks` owns the `['agent', 'rows', …]` key and the shape stored
// under it. The summary line is one projection of that shape.
import { useArtefactSummaries } from '@/lib/agent-rows/hooks';
import { AGENT_NAMES, TASK_TYPE_LABELS, routeForTask } from '@/components/agent/agent-task-meta';

// -------- Item types --------

export type ReviewItemKind =
    | 'call_drafts'
    | 'monthly_narrative'
    | 'coac_action_draft'
    | 'composer_draft'
    | 'task';

/** The seven brief-based composers whose `*Draft` rows render in the generic
 *  <CompositionWorkbench> (ADR-0010 / PRD #66). Re-exported from the contract,
 *  where the composer source list lives. */
export type { ComposerDraftAgent };

interface ReviewItemBase {
    /** Stable id used for React keys + URL `?focus=` deep links. */
    id: string;
    kind: ReviewItemKind;
    /** Short ALL-CAPS agent name (ATLAS, VIGIL, SCRIVENER, …) for the eyebrow. */
    agent: string;
    /** Human-readable client / subject for the row title. */
    primaryLabel: string;
    /** Secondary line — call subject, period, trigger detail, etc. */
    secondaryLabel: string;
    /** Frontend scope — sidebar filters on this. */
    frontend: AgentFrontend;
    /** Page route the row's "Open in context" jumps to. */
    contextHref: string;
    /** Total pending decisions still needed inside this item. */
    pendingCount: number;
}

export interface CallDraftsReviewItem extends ReviewItemBase {
    kind: 'call_drafts';
    callId: string;
    clientId: string | null;
    /** The PERSON the call resolved to (SEP-183 / ADR-0015). One man can hold
     *  several trusts, so an insight is filed under him and not under one of
     *  his Accounts. Null when the call resolved to no Contact, and null when
     *  the Contact sits outside the reader's book. */
    contactId: string | null;
    contactName: string | null;
    factCount: number;
    actionCount: number;
    noteCount: number;
    /**
     * The relationship proposals ATLAS raised on this call (SEP-215).
     *
     * They are carried WHOLE rather than counted, because the queue payload
     * already holds everything the body renders — the proposed name, the role,
     * the confidence, the quote and the ranked counterparty suggestions. The
     * other three streams re-fetch their rows through `/api/agent/rows`; this
     * one has no reason to.
     *
     * A proposal is a fourth decision on its own call, not a second item.
     */
    proposals: RelationshipProposalRow[];
}

export interface MonthlyNarrativeReviewItem extends ReviewItemBase {
    kind: 'monthly_narrative';
    draftId: string;
    clientId: string;
    period: string;
}

export interface ComposerDraftReviewItem extends ReviewItemBase {
    kind: 'composer_draft';
    agent: ComposerDraftAgent;
    /** Id of the `*Draft` row. */
    draftId: string;
    /** The `*_drafts` session-row table the draft lives in. Constrained to the
     *  contract-owned `/api/agent/rows` allow-list so a finding table can never
     *  be dereferenced through that raw surface. */
    table: AgentRowTable;
}

export interface TaskReviewItem extends ReviewItemBase {
    kind: 'task';
    task: AgentTask;
}

/** CEDAR's coac-action-draft — one item per draft row (a single CA PDF
 *  upload can emit several). Carries the full structured field set (not the
 *  generic summary row) because the review UI lets Ops correct the extracted
 *  fields before approving (ADR-0020). */
export interface CoacActionDraftReviewItem extends ReviewItemBase {
    kind: 'coac_action_draft';
    draftId: string;
    securityName: string;
    securityId: string | null;
    eventType: string;
    sourceReferenceId: string;
    /** The four key dates of the notice. Each one carries the verbatim text of
     *  the document, the reading of the server, and the reason for a failed
     *  reading (SEP-201). The review body shows the reading, and it stops an
     *  approval while a reading failed. */
    responseDeadline: CoacNoticeDate;
    exDate: CoacNoticeDate;
    recordDate: CoacNoticeDate;
    payDate: CoacNoticeDate;
    narrative: string | null;
    wealthManager: string | null;
    /** The election options that CEDAR read off the notice (SEP-251). The
     *  reviewer confirms or corrects them, and the approval carries the
     *  confirmed list to the live corporate action. Null says that nobody read
     *  a notice, and `[]` says that the notice prints no option. */
    electionOptions: NonNullable<CoacActionDraftRow['electionOptions']> | null;
}

export type ReviewQueueItem =
    | CallDraftsReviewItem
    | MonthlyNarrativeReviewItem
    | CoacActionDraftReviewItem
    | ComposerDraftReviewItem
    | TaskReviewItem;

export interface UseReviewQueueResult {
    items: ReviewQueueItem[];
    totalCount: number;
    /** Item count only — equal to `items.length`. Convenience for the badge. */
    itemCount: number;
    /**
     * Has the pending-artefact fetch resolved at least once (success or error)?
     *
     * `items` is `[]` both while the queue is still loading and when it is
     * genuinely empty, and callers that act on a *specific* item — the board's
     * `?focus=` deep link — must not read the first as the second. Until this
     * flips true, "no matching item" means "not yet", not "not there".
     */
    isSettled: boolean;
}

// -------- Supporting row shapes --------
//
// The three per-stream row shapes — `CallArtefactRow`, `MonthlyNarrativeRow`,
// `ArtefactSummaryRow` — are the contract DTOs (ADR-0003). The server reads
// fully-typed ontology rows and projects them onto these, so the hook
// consumes the real types end-to-end instead of re-asserting a local shape.

/**
 * Pull a one-line summary from an artefact row. Tries the common
 * content fields in priority order so the hook stays generic across
 * all 17 agent types. Falls back to `null` when the row is empty
 * (e.g. the runner stamped a pointer but the artefact row is still
 * propagating).
 *
 * Exported so the Kanban / TaskCard / ActivityFeed components can
 * reuse the same fallback rules when displaying the per-task summary
 * line. Pure / pointer-stable across re-renders so it's safe to call
 * inside `useMemo` deps.
 */
export function summariseArtefact(row: Record<string, unknown> | null): string | null {
    if (!row) return null;
    const pick = (key: string): string | null => {
        const v = row[key];
        return typeof v === 'string' && v.trim() ? v.trim() : null;
    };
    return (
        pick('summary') ??
        pick('headline') ??
        pick('subject') ??
        pick('claim') ??
        pick('signalSummary') ??
        pick('ruleStatement') ??
        firstVariantBody(row)
    );
}

/** First composer variant body (truncated), read defensively off a loose row. */
function firstVariantBody(row: Record<string, unknown>): string | null {
    const variants = row.variants;
    if (!Array.isArray(variants) || variants.length === 0) return null;
    const first = variants[0];
    if (!first || typeof first !== 'object') return null;
    const body = (first as Record<string, unknown>).body;
    if (typeof body !== 'string' || !body.trim()) return null;
    const trimmed = body.trim();
    return trimmed.length > 140 ? `${trimmed.slice(0, 137)}...` : trimmed;
}

// Drop a trailing "· Run <AGENT> now" call-to-action clause from a meta label
// (e.g. an unimplemented task's `triggerDetail` of "Ops · Run MASON now"),
// leaving just the leading area/persona ("Ops"). Applied once at the source so
// every surface that renders `secondaryLabel` (review panel, kanban card,
// rejection dialog subject) stays clean.
function stripRunNowCta(label: string | null | undefined): string {
    const value = label ?? '';
    const parts = value.split('·');
    if (parts.length > 1 && /run .* now/i.test(parts[parts.length - 1])) {
        return parts.slice(0, -1).join('·').trim();
    }
    return value;
}

/** The counted streams of one call, as the secondary line words them. */
function callSummaryLine(item: {
    factCount: number;
    actionCount: number;
    noteCount: number;
    proposals: ReadonlyArray<unknown>;
}): string {
    const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
    return [
        item.factCount > 0 && plural(item.factCount, 'fact'),
        item.actionCount > 0 && plural(item.actionCount, 'action'),
        item.noteCount > 0 && plural(item.noteCount, 'note'),
        item.proposals.length > 0 && plural(item.proposals.length, 'relationship'),
    ]
        .filter(Boolean)
        .join(' · ');
}

// -------- Hook --------

export function useReviewQueue(frontend?: AgentFrontend): UseReviewQueueResult {
    // The review queue is part of the owner-scoped worklist (ADR-0010), so the
    // task-derived review items use `worklistTasks` (runs I own / ownerless on
    // my surface), not the firm-wide activity feed.
    const { worklistTasks: tasks } = useAgent();

    // Single typed call replaces 14 generic `/api/db/:table` reads. The
    // server-side aggregator filters every artefact source to
    // `reviewStatus = 'pending'` and denormalises `clientName` so the
    // hook does not need a separate clients lookup.
    const { data, isFetched } = useQuery({
        queryKey: ['review-queue', 'pending'],
        queryFn: async () => {
            const res = await api.reviewQueue.pending();
            if (res.status !== 200) {
                throw new Error(`review-queue fetch failed: ${res.status}`);
            }
            return res.body;
        },
        refetchInterval: 10_000,
    });

    // Build a per-table id → row map so task → artefact lookup is O(1).
    // `register` accepts any of the three contract row DTOs: the post-call
    // and narrative rows (CallArtefactRow / MonthlyNarrativeRow) are
    // structurally a superset of the summary fields the lookup reads, so
    // they index alongside the summary rows with no cast.
    const artefactRowByTable = useMemo(() => {
        const m = new Map<string, Map<string, ArtefactSummaryRow>>();
        const register = (
            table: string,
            rows: ReadonlyArray<ArtefactSummaryRow | CallArtefactRow | MonthlyNarrativeRow>,
        ) => {
            const byId = new Map<string, ArtefactSummaryRow>();
            for (const r of rows) byId.set(r.id, r);
            m.set(table, byId);
        };
        if (!data) return m;
        register('wealth_vigil_findings', data.wealthVigilFindings);
        register('suitability_letter_drafts', data.suitabilityLetterDrafts);
        register('onboarding_pack_drafts', data.onboardingPackDrafts);
        register('client_communication_drafts', data.clientCommunicationDrafts);
        register('onboarding_stall_findings', data.onboardingStallFindings);
        register('vulnerability_marker_findings', data.vulnerabilityMarkerFindings);
        register('pcf_deviation_findings', data.pcfDeviationFindings);
        register('investment_trust_policy_findings', data.investmentTrustPolicyFindings);
        register('data_freshness_findings', data.dataFreshnessFindings);
        register('fund_report_drafts', data.fundReportDrafts);
        register('vulnerability_adaptation_plan_drafts', data.vulnerabilityAdaptationPlanDrafts);
        register('governance_paper_drafts', data.governancePaperDrafts);
        register('fair_value_assessment_drafts', data.fairValueAssessmentDrafts);
        // Post-call + narrative tables are already loaded above for the
        // grouped call_drafts / monthly_narrative items; surface them here
        // too so individual-task lookups by `artefactType` work uniformly.
        register('call_extracted_facts', data.callExtractedFacts);
        register('call_action_items', data.callActionItems);
        register('call_kb_notes', data.callKbNotes);
        register('wealth_monthly_narrative_drafts', data.wealthMonthlyNarrativeDrafts);
        return m;
    }, [data]);

    // Dereference a task to its artefact row, if any.
    const artefactFor = useMemo(() => {
        return (task: AgentTask): ArtefactSummaryRow | null => {
            const type = task.artefactType ?? null;
            const id = task.artefactId ?? null;
            if (!type || !id) return null;
            const table = artefactTableNameFor(type);
            if (!table) return null;
            return artefactRowByTable.get(table)?.get(id) ?? null;
        };
    }, [artefactRowByTable]);

    // Client name lookup — keyed by id so an item can show the client without
    // each consumer rebuilding the map. The server denormalises `clientName`
    // onto every row, so this just projects it back into a map.
    const clientNameById = useMemo(() => {
        const m = new Map<string, string>();
        if (!data) return m;
        for (const rows of Object.values(data)) {
            for (const row of rows as ReadonlyArray<{
                clientId?: string | null;
                clientName?: string | null;
            }>) {
                if (row.clientId && row.clientName) m.set(row.clientId, row.clientName);
            }
        }
        return m;
    }, [data]);

    const items = useMemo<ReviewQueueItem[]>(() => {
        // ONE rule, shared with the badge. This loop only dresses its output.
        const entries = data ? projectReviewQueue(data, tasks) : [];
        const out: ReviewQueueItem[] = [];

        for (const entry of entries) {
            switch (entry.kind) {
                case 'call_drafts': {
                    const clientName =
                        entry.clientName ??
                        (entry.clientId ? clientNameById.get(entry.clientId) : null) ??
                        'Unknown client';
                    out.push({
                        kind: 'call_drafts',
                        id: entry.id,
                        agent: 'ATLAS',
                        primaryLabel: clientName,
                        secondaryLabel: `Post-call drafts · ${callSummaryLine(entry)}`,
                        frontend: entry.frontend,
                        contextHref: `/desk/calls-meetings?call=${encodeURIComponent(entry.callId)}`,
                        pendingCount: entry.pendingCount,
                        callId: entry.callId,
                        clientId: entry.clientId,
                        contactId: entry.contactId,
                        contactName: entry.contactName,
                        factCount: entry.factCount,
                        actionCount: entry.actionCount,
                        noteCount: entry.noteCount,
                        proposals: entry.proposals,
                    });
                    break;
                }
                case 'monthly_narrative': {
                    const draft = entry.draft;
                    out.push({
                        kind: 'monthly_narrative',
                        id: entry.id,
                        agent: 'ATLAS',
                        primaryLabel: clientNameById.get(draft.clientId) ?? 'Unknown client',
                        secondaryLabel: `Monthly narrative · ${draft.period}`,
                        frontend: entry.frontend,
                        contextHref: `/desk/client-knowledge?client=${encodeURIComponent(draft.clientId)}`,
                        pendingCount: entry.pendingCount,
                        draftId: draft.id,
                        clientId: draft.clientId,
                        period: draft.period,
                    });
                    break;
                }
                case 'coac_action_draft': {
                    const draft = entry.draft;
                    out.push({
                        kind: 'coac_action_draft',
                        id: entry.id,
                        agent: 'CEDAR',
                        primaryLabel: draft.securityName,
                        secondaryLabel: `CA extraction · ${draft.eventType}`,
                        frontend: entry.frontend,
                        // A draft has no corporate action row until Ops approves it, so `sel`
                        // cannot name it. The page reads `tab` and `bucket`: the COAC Mailbox
                        // "for review" bucket holds the work that waits for Ops.
                        contextHref: '/ops/corporate-actions?tab=coac-mailbox&bucket=for_review',
                        pendingCount: entry.pendingCount,
                        draftId: draft.id,
                        securityName: draft.securityName,
                        securityId: draft.securityId,
                        eventType: draft.eventType,
                        sourceReferenceId: draft.sourceReferenceId,
                        responseDeadline: draft.responseDeadline,
                        exDate: draft.exDate,
                        recordDate: draft.recordDate,
                        payDate: draft.payDate,
                        narrative: draft.narrative,
                        electionOptions: draft.electionOptions,
                        wealthManager: draft.wealthManager,
                    });
                    break;
                }
                case 'composer_draft': {
                    const { source } = entry;
                    const clientName =
                        (entry.clientId ? clientNameById.get(entry.clientId) : null) ??
                        entry.task?.clientName ??
                        source.label;
                    out.push({
                        kind: 'composer_draft',
                        id: entry.id,
                        agent: source.agent,
                        primaryLabel: clientName,
                        secondaryLabel: `${source.label} draft`,
                        frontend: entry.frontend,
                        // `?focus=` matches the review-item id, so prefix it the
                        // same way — the queue auto-expands the workbench on
                        // landing.
                        contextHref: `/${source.frontend}/agents?focus=${encodeURIComponent(entry.id)}`,
                        pendingCount: entry.pendingCount,
                        draftId: entry.draftId,
                        table: source.table,
                    });
                    break;
                }
                case 'task': {
                    const { task } = entry;
                    const typeLabel = TASK_TYPE_LABELS[task.type] ?? task.type;
                    // Prefer the artefact's emitted summary over the templated
                    // `triggerDetail` (PRD #65 user story 1) — falling back to
                    // the legacy field for AEGIS/MASON/CEDAR tasks whose
                    // artefacts live on legacy tables not yet ported.
                    const artefactSummary = summariseArtefact(artefactFor(task));
                    out.push({
                        kind: 'task',
                        id: entry.id,
                        agent: AGENT_NAMES[task.type] ?? task.type,
                        primaryLabel: task.clientName ?? typeLabel,
                        secondaryLabel: stripRunNowCta(
                            artefactSummary ?? (task.clientName ? typeLabel : task.triggerDetail),
                        ),
                        frontend: entry.frontend,
                        contextHref: routeForTask(task),
                        pendingCount: entry.pendingCount,
                        task,
                    });
                    break;
                }
            }
        }

        // Apply the frontend filter last so the underlying data work runs once.
        if (frontend) return out.filter((it) => it.frontend === frontend);
        return out;
    }, [data, tasks, clientNameById, artefactFor, frontend]);

    const totalCount = useMemo(() => items.reduce((sum, it) => sum + it.pendingCount, 0), [items]);

    return { items, totalCount, itemCount: items.length, isSettled: isFetched };
}

/**
 * Resolve a single AgentTask to its artefact row's summary line.
 * Returns `null` when the task has no artefact pointer or the
 * artefact row hasn't propagated to the client yet — callers fall
 * back to `task.triggerDetail` in that case.
 *
 * Implemented over `useArtefactSummaries`, which owns the
 * `['agent', 'rows', …]` key and the shape stored under it. It therefore
 * issues at most one round-trip per (table, session), and it shares that one
 * cache entry with every other agent-row surface.
 */
export function useArtefactSummary(task: AgentTask): string | null {
    // `agentRowTableFor` (not `artefactTableNameFor`) so only tables the raw
    // `/api/agent/rows/:table` surface actually serves resolve to non-null:
    // finding tasks map to null here, skip the query (which 400s for them), and
    // fall back to the trigger detail — exactly as they already do today.
    const table = agentRowTableFor(task.artefactType ?? null);
    const rows = useArtefactSummaries(table);
    return useMemo(() => {
        if (!table || !task.artefactId) return null;
        const row = rows.find((r) => r.id === task.artefactId) ?? null;
        return summariseArtefact(row);
    }, [rows, table, task.artefactId]);
}
